import {
  ConductSelectTypesFilterValue,
  ConductVisibility,
  DEFAULT_DATE_FORMAT_FNS,
  FilterKeys,
  GetConductEntriesQueryFilters,
} from '@schooly/api';
import {
  applySelectedConductTypeId,
  applySelectedConductTypeOption,
  getSelectedItemsWithGrouping,
  SelectedItem,
  SelectedItemWithGrouping,
} from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../hooks/useSchool';

export type UseConductFiltersProps = {
  schoolId: string;
  filters: GetConductEntriesQueryFilters;
  onSetFilters: (filters: GetConductEntriesQueryFilters) => void;
};

export const useConductFilters = ({ schoolId, filters, onSetFilters }: UseConductFiltersProps) => {
  const ageGroups = useAgeGroups({
    schoolId: schoolId,
    userType: SchoolUserRole.Student,
  });
  const { getAgeGroupById, schoolLevelsWithAgeGroupsMap } = ageGroups;
  const { hasHouses } = useSchool();
  const { formatMessage } = useIntl();

  const actions = useMemo(() => {
    const clearFilter = (key: keyof typeof filters) => () => {
      onSetFilters({
        ...filters,
        [key]: [],
      });
    };

    const updateFilter =
      (
        key:
          | FilterKeys.Group
          | FilterKeys.House
          | FilterKeys.Student
          | FilterKeys.ConductType
          | FilterKeys.AgeGroup,
      ) =>
      (v: string[]) => {
        onSetFilters({
          ...filters,
          [key]: v,
        });
      };

    const onSetConductTypeId = (id: string) => {
      onSetFilters(applySelectedConductTypeId(id)(filters));
    };

    const onClearConductType = () => {
      onSetFilters({
        ...filters,
        [FilterKeys.ConductType]: [],
        [FilterKeys.ConductSelectTypes]: [],
      });
    };

    const onSetConductTypesAndConductTypeOptions = (
      ids: string[],
      options: ConductSelectTypesFilterValue[],
    ) => {
      onSetFilters({
        ...filters,
        [FilterKeys.ConductSelectTypes]: options,
        [FilterKeys.ConductType]: ids,
      });
    };

    const onSetConductTypeOption = (conductTypeId: string, option: string) => {
      onSetFilters(applySelectedConductTypeOption(conductTypeId, option)(filters));
    };

    const onSelectConductStatus = (v: ConductVisibility[]) =>
      onSetFilters({
        ...filters,
        [FilterKeys.ConductStatus]: v,
      });

    return {
      onSetDate: (v: [Date, Date]) =>
        onSetFilters({
          ...filters,
          [FilterKeys.Date]: [
            format(v[0], DEFAULT_DATE_FORMAT_FNS),
            format(v[1], DEFAULT_DATE_FORMAT_FNS),
          ],
        }),
      onSetAgeGroup: updateFilter(FilterKeys.AgeGroup),
      onClearAgeGroup: clearFilter(FilterKeys.AgeGroup),
      onSetHouseId: updateFilter(FilterKeys.House),
      onClearHouse: clearFilter(FilterKeys.House),
      onSetConductTypeId,
      onClearConductType,
      onSelectConductStatus,
      onSetConductTypesAndConductTypeOptions,
      onClearConductStatus: clearFilter(FilterKeys.ConductStatus),
      onSetGroup: updateFilter(FilterKeys.Group),
      onClearGroup: clearFilter(FilterKeys.Group),
      onSetStudentId: updateFilter(FilterKeys.Student),
      onClearStudent: clearFilter(FilterKeys.Student),
      onSetConductTypeOption,
    };
  }, [filters, onSetFilters]);

  const filtersDate = filters.date;
  const filtersGroup = filters.group;
  const filtersAgeGroup = filters.age_group;
  const filtersHouse = filters.house;
  const filtersVisibility = filters.visibility;
  const filtersStudent = filters.student;
  const filtersConductType = filters.conduct_type;
  const filtersConductSelectTypes = filters.conduct_select_types;

  const labels = {
    [FilterKeys.Date]: formatMessage({ id: 'schoolProperty-Period' }),
    [FilterKeys.ConductStatus]: formatMessage({ id: 'visibility' }),
    [FilterKeys.ConductType]: formatMessage({ id: 'conduct-ConductType' }),
    [FilterKeys.Student]: formatMessage({ id: 'schoolProperty-Student' }),
    [FilterKeys.AgeGroup]: formatMessage({ id: 'schoolProperty-AgeGroup' }),
    [FilterKeys.House]: formatMessage({ id: 'schoolProperty-House' }),
    [FilterKeys.Group]: formatMessage({ id: 'asssessments-Group' }),
  };

  const selectedItemsForAgeGroups: SelectedItemWithGrouping[] = useMemo(() => {
    const selectedAgeGroups =
      filtersAgeGroup?.reduce<SelectedItem[]>((acc, id) => {
        const ageGroup = getAgeGroupById(id);

        return ageGroup
          ? [
              ...acc,
              {
                id: ageGroup.id,
                groupId: ageGroup.level_id,
              },
            ]
          : acc;
      }, []) ?? [];

    return getSelectedItemsWithGrouping(selectedAgeGroups, schoolLevelsWithAgeGroupsMap);
  }, [filtersAgeGroup, schoolLevelsWithAgeGroupsMap, getAgeGroupById]);

  return {
    ...ageGroups,
    ...actions,
    labels,
    hasHouses,
    selectedItemsForAgeGroups,
    filtersDate,
    filtersGroup,
    filtersAgeGroup,
    filtersHouse,
    filtersVisibility,
    filtersStudent,
    filtersConductType,
    filtersConductSelectTypes,
  };
};
